import type { ChangeEvent } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "../../../../lib/styled";
import { useChoiceGroup } from "./useChoiceGroup";

export interface ChoiceGroupProps {
  options: string[];
  /** The selected options. A single-choice group shows its first one. */
  value: string[];
  /** Several options may be selected (checkboxes); otherwise exactly one (radio buttons). */
  multiple: boolean;
  /** The new selection, derived from the current value and the change event. */
  onChange: (next: string[]) => void;
  /** The raw change event of an option's input, for callers that need more than the selection. */
  onRawChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Hover text for an option; nothing is shown when it returns undefined or an empty string. */
  renderTooltip?: (option: string) => string | undefined;
  /**
   * The inputs ignore the pointer and the whole row takes the click instead (still reported
   * through onChange/onRawChange). Keeps the inputs from taking focus — and, for radios, from
   * handling the arrow keys themselves — when something else owns keyboard handling.
   */
  disablePointerEvents?: boolean;
  /** Index of the highlighted row; the list scrolls to keep it visible. */
  focusedIndex?: number;
  /** The highlighted row changed — by a click, or by the arrow keys when keyboardNavigation is on. */
  onFocusChange?: (index: number) => void;
  /** ↑/↓ move the highlighted row and Space selects it, while the group is mounted. */
  keyboardNavigation?: boolean;
  className?: string;
}

const Container = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: `${theme.shape.borderRadius}px`,
  maxHeight: 280,
  overflowY: "auto",
}));

const Row = styled(Stack)<{
  $focused: boolean;
  $clickable: boolean;
}>(({ theme, $focused, $clickable }) => ({
  alignItems: "center",
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  cursor: "pointer",
  backgroundColor: $focused ? theme.palette.action.selected : "transparent",
  border: `2px solid ${$focused ? theme.palette.primary.main : "transparent"}`,
  borderRadius: `${theme.shape.borderRadius}px`,
  "&:hover": { backgroundColor: theme.palette.action.hover },
  "& .MuiFormControlLabel-root": {
    margin: 0,
    flex: 1,
    pointerEvents: $clickable ? "none" : undefined,
  },
}));

/**
 * A bordered, scrollable list of options rendered as checkboxes when several may be selected and
 * as radio buttons when exactly one may be, with a highlighted row and optional keyboard
 * navigation.
 */
export function ChoiceGroup({
  options,
  value,
  multiple,
  onChange,
  onRawChange,
  renderTooltip,
  disablePointerEvents = false,
  focusedIndex,
  onFocusChange,
  keyboardNavigation = false,
  className,
}: ChoiceGroupProps) {
  const { focused, rowRef, inputRef, handleChange, clickInput } = useChoiceGroup({
    options,
    value,
    multiple,
    onChange,
    onRawChange,
    focusedIndex,
    onFocusChange,
    keyboardNavigation,
  });

  const items = options.map((option, i) => (
    // Empty title renders no tooltip, so options without one are unaffected.
    <Tooltip key={option} title={renderTooltip?.(option) ?? ""} placement="left">
      <Row
        direction="row"
        ref={rowRef(i)}
        $focused={i === focused}
        $clickable={disablePointerEvents}
        onClick={(event) => {
          // The programmatic click below bubbles back up to this row.
          if (event.target instanceof HTMLInputElement) return;
          onFocusChange?.(i);
          // Programmatic, so the change reaches onChange/onRawChange like a click on the input.
          if (disablePointerEvents) clickInput(i);
        }}
      >
        <FormControlLabel
          value={option}
          label={option}
          control={
            multiple ? (
              <Checkbox
                size="small"
                tabIndex={-1}
                inputRef={inputRef(i)}
                checked={value.includes(option)}
                onChange={(event) => handleChange(option, event)}
              />
            ) : (
              <Radio size="small" tabIndex={-1} inputRef={inputRef(i)} />
            )
          }
        />
      </Row>
    </Tooltip>
  ));

  return (
    <Container className={className}>
      {multiple ? (
        <FormGroup>{items}</FormGroup>
      ) : (
        <RadioGroup
          value={value[0] ?? ""}
          onChange={(event) => handleChange(event.target.value, event)}
        >
          {items}
        </RadioGroup>
      )}
    </Container>
  );
}
